import React, { useContext, useState } from 'react'
import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import AnDateField from '../../../components/DateTime/DateField'
import API from '../../../services/API'
import { GlobalContext } from '../../../App'

const ACTIVITY_TYPES = [
  { value: 'treatment_of_disease_disorder_injury', label: 'Treatment of disease, disorder or injury (TDDI)' },
  { value: 'transport_services_triage_medical_advice', label: 'Transport services, triage and medical advice' },
]

const STATUS_OPTIONS = [
  'not_registered',
  'preparing_application',
  'applied',
  'registered',
  'exempt',
  'not_applicable',
]

const ActivityFormDialog = ({ activity, onClose, onSaved }) => {
  const { setToast } = useContext(GlobalContext)
  const [form, setForm] = useState({
    activityType: activity?.activityType || 'treatment_of_disease_disorder_injury',
    locationLabel: activity?.locationLabel || '',
    registrationStatus: activity?.registrationStatus || 'not_registered',
    cqcProviderId: activity?.cqcProviderId || '',
    cqcLocationId: activity?.cqcLocationId || '',
    registeredSince: activity?.registeredSince || null,
    applicationSubmittedDate: activity?.applicationSubmittedDate || null,
    notes: activity?.notes || '',
  })
  const [saving, setSaving] = useState(false)

  const update = (field) => (value) => setForm((prev) => ({ ...prev, [field]: value }))

  const save = () => {
    if (!form.locationLabel.trim()) {
      setToast?.({ message: 'A location is required', severity: 'error' })
      return
    }
    setSaving(true)
    const request = activity
      ? API.put(`/registration/activities/${activity._id}`, form)
      : API.post('/registration/activities', form)

    request
      .then((res) => {
        if (res.status === 200 || res.status === 201) {
          setToast?.({ message: activity ? 'Activity updated' : 'Activity added' })
          onSaved?.()
        } else {
          setToast?.({ message: res.data?.error || 'Unable to save', severity: 'error' })
        }
      })
      .catch((e) => console.log(e))
      .finally(() => setSaving(false))
  }

  return (
    <Stack gap={2} sx={{ p: 2, minWidth: { sm: 420 } }}>
      <Typography variant="title2">{activity ? 'Edit Regulated Activity' : 'Add Regulated Activity'}</Typography>

      <FormControl size="small" fullWidth>
        <InputLabel>Activity type</InputLabel>
        <Select
          label="Activity type"
          value={form.activityType}
          onChange={(e) => update('activityType')(e.target.value)}
        >
          {ACTIVITY_TYPES.map((a) => (
            <MenuItem key={a.value} value={a.value}>
              {a.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        size="small"
        label="Location / premises"
        value={form.locationLabel}
        onChange={(e) => update('locationLabel')(e.target.value)}
        fullWidth
        required
      />

      <FormControl size="small" fullWidth>
        <InputLabel>Registration status</InputLabel>
        <Select
          label="Registration status"
          value={form.registrationStatus}
          onChange={(e) => update('registrationStatus')(e.target.value)}
        >
          {STATUS_OPTIONS.map((s) => (
            <MenuItem key={s} value={s}>
              {s.replaceAll('_', ' ')}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Stack direction="row" gap={2}>
        <TextField
          size="small"
          label="CQC Provider ID"
          value={form.cqcProviderId}
          onChange={(e) => update('cqcProviderId')(e.target.value)}
          fullWidth
        />
        <TextField
          size="small"
          label="CQC Location ID"
          value={form.cqcLocationId}
          onChange={(e) => update('cqcLocationId')(e.target.value)}
          fullWidth
        />
      </Stack>

      <Stack direction="row" gap={2}>
        <AnDateField
          label="Registered since"
          date={form.registeredSince}
          onChange={(m) => update('registeredSince')(m ? m.toISOString() : null)}
        />
        <AnDateField
          label="Application submitted"
          date={form.applicationSubmittedDate}
          onChange={(m) => update('applicationSubmittedDate')(m ? m.toISOString() : null)}
        />
      </Stack>

      <TextField
        size="small"
        label="Notes"
        value={form.notes}
        onChange={(e) => update('notes')(e.target.value)}
        fullWidth
        multiline
        minRows={2}
      />

      <Stack direction="row" justifyContent="flex-end" gap={1}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </Stack>
    </Stack>
  )
}

export default ActivityFormDialog
